import type { Coordinate, GoogleGeocoderAddressComponent, GooglePlaceResult, PageableContent, PageableContentAPI, TimestampInMillis } from '@cobliteam/common-types';
import moment from 'moment';
import { driverGraphQLAPIToDriver } from '../../driver/APITransformer';
import {
  type ActivitiesSummary,
  type ActivitiesSummaryAPI,
  type ExecutionDigest,
  type ExecutionDigestAPI,
  GeocoderAddressComponentTypes,
  type InputActivity,
  type InputActivityAPI,
  type InputDestinationAPI,
  type NewRoute,
  type NewRouteAPI,
  type NewRouteReport,
  type NewRouteReportAPI,
  type OperationSummary,
  type OperationSummaryAPI,
  type PropagationType,
  type RouteBreak,
  type RouteBreakAPI,
  type RouteRecurrency,
  type RouteRecurrencyAPI,
  type RouteSettings,
  type RouteSettingsAPI,
  type RoutesSummary,
  type RoutesSummaryAPI,
  type RouteWorkingHours,
  type RouteWorkingHoursAPI,
  type SpreadsheetImportResultAddressAPI,
  type ValidatedSpreadsheet,
  type ValidatedSpreadsheetAPI,
} from '../Definitions';
import { defaultPlace, getAddressComponent, getFormattedAddress } from '../utils';
import { activityTransformer, reportActivityTransformer } from './ActivitiesTransformers';

const breakTransformer = (breakAPI: RouteBreakAPI): RouteBreak => {
  const { start_time: startTime, end_time: endTime, duration } = breakAPI;

  return {
    startTime,
    endTime,
    duration,
  };
};

const routeWorkingHoursTransformer = (routeWorkingHoursAPI: RouteWorkingHoursAPI): RouteWorkingHours => {
  const { end_time: endTime, start_time: startTime } = routeWorkingHoursAPI;

  return { endTime, startTime };
};

const routeSettingsTransformer = (routeSettingsAPI: RouteSettingsAPI): RouteSettings => {
  const { break_window: breakWindow, objectives, routing_type: routingType, working_hours: workingHours } = routeSettingsAPI;

  return {
    breakWindow: breakWindow ? breakTransformer(breakWindow) : undefined,
    objectives,
    routingType,
    workingHours: routeWorkingHoursTransformer(workingHours),
  };
};

const routeRecurrencyTransformer = (routeRecurrency: RouteRecurrencyAPI): RouteRecurrency => {
  const { id, frequency, repeat_interval, days_of_week, days_of_month, until_date, week_numbers } = routeRecurrency;

  return {
    id,
    repeatInterval: repeat_interval,
    frequency,
    daysOfMonth: days_of_month,
    weekNumbers: week_numbers,
    daysOfWeek: days_of_week,
    untilDate: until_date,
  };
};

export const routeTransformer = (routeAPI: NewRouteAPI): NewRoute => {
  const {
    id,
    name,
    activities,
    points,
    type,
    status,
    planned_duration: plannedDuration,
    planned_distance_in_meters: plannedDistanceInMeters,
    created_at: createdAt,
    updated_at: updatedAt,
    start_time: startTime,
    end_time: endTime,
    vehicle_id: vehicleId,
    driver_id: driverId,
    device_id: deviceId,
    shared_id: sharedId,
    driver_name: driverName,
    break: routeBreak,
    executed_end_time: executedEndTime,
    executed_start_time: executedStartTime,
    settings,
    recurrency,
    propagation_type: propagationType,
  } = routeAPI;

  return {
    id,
    name,
    activities: activities.map(activityTransformer),
    points,
    type,
    status,
    plannedDuration,
    plannedDistanceInMeters,
    createdAt,
    updatedAt,
    startTime,
    break: routeBreak ? breakTransformer(routeBreak) : undefined,
    settings: settings ? routeSettingsTransformer(settings) : undefined,
    recurrency: recurrency ? routeRecurrencyTransformer(recurrency) : undefined,
    propagationType: propagationType as PropagationType,
    ...(endTime && { endTime }),
    ...(vehicleId && { vehicleId }),
    ...(driverId && { driverId }),
    ...(deviceId && { deviceId }),
    ...(sharedId && { sharedId }),
    ...(driverName && { driverName }),
    ...(executedEndTime && { executedEndTime }),
    ...(executedStartTime && { executedStartTime }),
  };
};

export const routePageTransformer = (page: PageableContentAPI<NewRouteAPI>): PageableContent<NewRoute> => ({
  content: page.content.map(routeTransformer),
  last: page.last,
  totalPages: page.total_pages,
  totalElements: page.total_elements,
  numberOfElements: page.number_of_elements,
  number: page.number,
  size: page.size,
  first: page.first,
  empty: page.empty,
});

const addressComponentTransformer = (inputDestinationAPI: InputDestinationAPI): GoogleGeocoderAddressComponent[] => {
  const {
    city,
    country,
    neighborhood,
    postal_code: postalCode,
    state,
    street_address: streetAddress,
    street_complement: streetComplement,
    street_number: streetNumber,
  } = inputDestinationAPI;

  return [
    city ? getAddressComponent(GeocoderAddressComponentTypes.CITY, city) : undefined,
    country ? getAddressComponent(GeocoderAddressComponentTypes.COUNTRY, country) : undefined,
    neighborhood ? getAddressComponent(GeocoderAddressComponentTypes.NEIGHBORHOOD, neighborhood) : undefined,
    postalCode ? getAddressComponent(GeocoderAddressComponentTypes.POSTAL_CODE, postalCode) : undefined,
    state ? getAddressComponent(GeocoderAddressComponentTypes.STATE, state) : undefined,
    streetAddress ? getAddressComponent(GeocoderAddressComponentTypes.STREET_ADDRESS, streetAddress) : undefined,
    streetComplement ? getAddressComponent(GeocoderAddressComponentTypes.STREET_COMPLEMENT, streetComplement) : undefined,
    streetNumber ? getAddressComponent(GeocoderAddressComponentTypes.STREET_NUMBER, streetNumber) : undefined,
  ].filter((value) => value !== undefined) as GoogleGeocoderAddressComponent[];
};

const geometryTransformer = (coordinate: Coordinate) => {
  const location = new google.maps.LatLng(coordinate.latitude, coordinate.longitude);
  const viewport = new google.maps.LatLngBounds(location);

  return { location, viewport };
};

const placeResultTransformer = (inputDestinationAPI: InputDestinationAPI): GooglePlaceResult => {
  const { coordinate, street_address: streetAddress } = inputDestinationAPI;

  return {
    name: streetAddress || '',
    geometry: geometryTransformer(coordinate),
    address_components: addressComponentTransformer(inputDestinationAPI),
    formatted_address: getFormattedAddress(inputDestinationAPI),
  };
};

const adjusTimeWindowOffset = (timeWindow: TimestampInMillis) => {
  return moment(timeWindow).subtract(moment().utcOffset(), 'minutes').valueOf();
};

export const inputActivityTransformer = (inputActivityAPI: InputActivityAPI, queryAddressAPI?: string): InputActivity => {
  const {
    destination,
    additional_info: additionalInfo,
    duration,
    name,
    load_size,
    time_windows: timeWindows,
    phone_number: phoneNumber,
    start_time: startTime,
    start_date: startDate,
    start_hour: startHour,
  } = inputActivityAPI;

  const startTimeWindow = (timeWindows?.[0]?.earliest && adjusTimeWindowOffset(timeWindows[0].earliest)) || undefined;
  const endTimeWindow = (timeWindows?.[0]?.latest && adjusTimeWindowOffset(timeWindows[0].latest)) || undefined;

  const destinationFromActivityAPI = destination ? placeResultTransformer(destination) : { ...defaultPlace, formatted_address: queryAddressAPI };

  return {
    destination: destinationFromActivityAPI,
    ...(startTime && { startTime }),
    ...(additionalInfo && { additionalInfo }),
    ...(duration && { duration }),
    ...(name && { name }),
    ...(load_size && { loadSize: load_size }),
    ...(startHour && { startHour }),
    startDate: startDate ? moment(startDate, 'DD/MM/YYYY').toDate().getTime() : moment().startOf('day').valueOf(),
    startTimeWindow: startTimeWindow ? moment(startTimeWindow).format('HH:mm') : undefined,
    endTimeWindow: endTimeWindow ? moment(endTimeWindow).format('HH:mm') : undefined,
    phoneNumber: phoneNumber ? `55${phoneNumber}` : undefined,
  };
};

export const validatedSpreadsheetTransformer = (validatedSpreadsheet: ValidatedSpreadsheetAPI): ValidatedSpreadsheet => ({
  activities: validatedSpreadsheet.activities.map((activity) => inputActivityTransformer(activity)),
  invalidAddresses: validatedSpreadsheet.invalid_addresses,
});

export const spreadsheetImportResultAddressTransformer = (spreadsheetImportResultAddress: SpreadsheetImportResultAddressAPI): InputActivity => {
  const { query_address: queryAddress, activity } = spreadsheetImportResultAddress;

  return activity ? inputActivityTransformer(activity, queryAddress) : { destination: { ...defaultPlace, formatted_address: queryAddress } };
};

export const routeReportTransformer = (routeReport: NewRouteReportAPI): NewRouteReport => {
  const { activities, distance, start_time: startTime, status, driver, name, vehicle, id, end_time: endTime, duration } = routeReport;

  return {
    id,
    activities: activities.map(reportActivityTransformer),
    driver: driver ? driverGraphQLAPIToDriver(driver) : undefined,
    vehicle,
    startTime,
    endTime,
    distance,
    duration,
    ...(status && { status }),
    ...(name && { name }),
  };
};

const executionDigestTransformer = (executionDigestAPI: ExecutionDigestAPI): ExecutionDigest => {
  const { ids, total } = executionDigestAPI;

  return { ids, total };
};

const routesSummaryTransformer = (routesSummaryAPI: RoutesSummaryAPI): RoutesSummary => {
  const { routes_with_vehicle, routes_without_vehicle, total } = routesSummaryAPI;

  return {
    total,
    routesWithVehicle: executionDigestTransformer(routes_with_vehicle),
    routesWithoutVehicle: executionDigestTransformer(routes_without_vehicle),
  };
};

export const activitiesSummaryTransformer = (activitiesSummaryAPI: ActivitiesSummaryAPI): ActivitiesSummary => {
  const { completed, not_completed, total, waiting } = activitiesSummaryAPI;

  return {
    total,
    completed: executionDigestTransformer(completed),
    notCompleted: executionDigestTransformer(not_completed),
    waiting: executionDigestTransformer(waiting),
  };
};

export const operationSummaryTransformer = (summary: OperationSummaryAPI): OperationSummary => {
  const { activities, routes } = summary;

  return {
    activities: activitiesSummaryTransformer(activities),
    routes: routesSummaryTransformer(routes),
  };
};
